/**
 * 이상한 방 - 모두의 방 (순수 DOM)
 *
 * 근거 문서: 로드맵 M13(메뉴 - 이 화면의 자리) · M14(공개·비공개·겨루기) · GLOSSARY 1.5절
 *
 * ## 메뉴의 아래층이다
 *
 * 메뉴가 "무엇을 할 것인가"를 묻고, 이 화면은 그중 여럿이를 골랐을 때 **어떤 여럿이인가**를 묻는다.
 * 두 화면이 같은 바탕(`menu.css`)을 입는 이유가 그것이다 - 사람에게는 한 화면의 두 층이다.
 *
 * ## 셋이고, 그중 하나는 판이다
 *
 * 아무나 한 판(공개 - 열린 방 목록)과 친구와 한 판(비공개 - 방을 만들거나 번호로 들어간다)과 겨루기(잠긴 판)다.
 * 앞의 둘이 여는 곳이 다르다는 것이 이 화면의 존재 이유다 - 공개는 **목록**(`ui/roomBrowser.ts`)을 열고
 * 비공개는 곧바로 폼을 연다. 둘이 같은 곳으로 가면 카드 둘이 같은 문이 되고, 그건 화면이 거짓말을 하는 것이다.
 *
 * ## 준비중은 버튼이 아니라 판이다
 *
 * 겨루기는 `div`로 서고 오른쪽 칸이 왜 잠겼는지를 말한다(M13 규칙 4 · GLOSSARY 3절 · `locker.ts`의
 * `createLockedPlate`와 같은 모양). 눌리지 않는 버튼은 고장 난 버튼과 구별되지 않는다.
 *
 * ## 계산하지 않는다
 *
 * 이 화면은 방도 좌석도 모른다. 카드를 누르면 손잡이를 부를 뿐이고, 방을 만드는 것도 번호로
 * 들어가는 것도 `app/lobbyFlow.ts`의 일이다.
 */

import type { BotDifficulty } from '../game/match/cpu'
import { t } from '../i18n'

import { createElement, setText } from './dom'
import './menu.css'

/* --- 입력 계약 ----------------------------------------------------------------- */

export type PartyHubHandlers = Readonly<{
  /** 아무나 한 판(M14). 열린 방 목록을 연다 - 거기서 고르거나 새 공개 방을 만든다. */
  onPublic: () => void
  /** 친구와 한 판. 방을 만들거나 번호로 들어가는 로비를 연다. */
  onPrivate: () => void
  /** 메뉴로 돌아간다. 뒤로는 언제나 메뉴다(로드맵 M13). */
  onBack: () => void
}>

export type PartyHubOptions = Readonly<{
  mount: HTMLElement
  handlers: PartyHubHandlers
  /** 카드가 지킬 수 있는 약속 둘 - 방 정원과, 빈 자리를 채우는 이웃의 성격. */
  seatsMax: number
  /** 난이도 이름은 `party.level.<difficulty>`로 조립한다 - 숫자(N, p, q)를 화면에 적지 않는다. */
  neighborLevel: BotDifficulty
}>

export type PartyHubHandle = Readonly<{
  show: () => void
  hide: () => void
  dispose: () => void
}>

/* --- 본체 ---------------------------------------------------------------------- */

export function createPartyHub(options: PartyHubOptions): PartyHubHandle {
  const { handlers } = options

  const root = createElement('div', 'ph-root')
  root.hidden = true
  root.setAttribute('role', 'dialog')
  root.setAttribute('aria-modal', 'true')
  root.setAttribute('aria-labelledby', 'ph-title')

  const head = createElement('header', 'mn-head')
  const eyebrow = createElement('p', 'mn-eyebrow', '')
  const title = createElement('h1', 'mn-title', '')
  title.id = 'ph-title'
  head.append(eyebrow, title)

  const cards = createElement('div', 'mn-cards')

  /* --- 아무나 한 판 --- */

  const publicCard = createElement('button', 'mn-card') as HTMLButtonElement
  publicCard.type = 'button'
  const publicEyebrow = createElement('span', 'mn-card__eyebrow', '')
  publicEyebrow.setAttribute('aria-hidden', 'true')
  const publicBody = createElement('span', 'mn-card__body')
  publicBody.setAttribute('aria-hidden', 'true')
  const publicTitle = createElement('span', 'mn-card__title', '')
  const publicDesc = createElement('span', 'mn-card__status', '')
  publicBody.append(publicTitle, publicDesc)
  const publicGo = createElement('span', 'mn-card__go', '')
  publicGo.setAttribute('aria-hidden', 'true')
  publicCard.append(publicEyebrow, publicBody, publicGo)
  publicCard.addEventListener('click', handlers.onPublic)

  /* --- 친구와 한 판 --- */

  const privateCard = createElement('button', 'mn-card') as HTMLButtonElement
  privateCard.type = 'button'
  const privateEyebrow = createElement('span', 'mn-card__eyebrow', '')
  privateEyebrow.setAttribute('aria-hidden', 'true')
  const privateBody = createElement('span', 'mn-card__body')
  privateBody.setAttribute('aria-hidden', 'true')
  const privateTitle = createElement('span', 'mn-card__title', '')
  const privateMeta = createElement('span', 'mn-card__meta')
  const privateSeats = createElement('span', 'mn-chip', '')
  const privateLevel = createElement('span', 'mn-chip', '')
  privateMeta.append(privateSeats, privateLevel)
  const privateDesc = createElement('span', 'mn-card__status', '')
  privateBody.append(privateTitle, privateMeta, privateDesc)
  const privateGo = createElement('span', 'mn-card__go', '')
  privateGo.setAttribute('aria-hidden', 'true')
  privateCard.append(privateEyebrow, privateBody, privateGo)
  privateCard.addEventListener('click', handlers.onPrivate)

  /* --- 겨루기 (잠긴 판) --- */

  const ranked = createElement('div', 'mn-card is-locked')
  const rankedEyebrow = createElement('span', 'mn-card__eyebrow', '')
  rankedEyebrow.setAttribute('aria-hidden', 'true')
  const rankedBody = createElement('span', 'mn-card__body')
  const rankedTitle = createElement('span', 'mn-card__title', '')
  const rankedDesc = createElement('span', 'mn-card__status', '')
  rankedBody.append(rankedTitle, rankedDesc)
  // 왜 잠겼는지를 말하는 칸. 값이 이유다(`locker.createLockedPlate`와 같은 모양).
  const rankedWhy = createElement('span', 'mn-card__go', '')
  ranked.append(rankedEyebrow, rankedBody, rankedWhy)

  cards.append(publicCard, privateCard, ranked)

  const back = createElement('button', 'mn-back', '') as HTMLButtonElement
  back.type = 'button'
  back.addEventListener('click', handlers.onBack)

  root.append(head, cards, back)
  options.mount.append(root)

  /**
   * Esc로 메뉴로. 판이 하나뿐인 화면에서 뒤로 가는 길이 버튼 하나뿐이면 키보드에서는 탭으로
   * 카드를 지나야 한다. 설정 패널이 위에 떠 있으면 Esc는 그쪽의 것이다 - 여기서 받으면
   * 패널을 닫으면서 동시에 화면도 옮겨 두 가지 일이 한 번에 일어난다(`levelSelect`와 같은 규약).
   */
  function handleKeyDown(event: KeyboardEvent): void {
    if (root.hidden || event.key !== 'Escape') return
    if (event.altKey || event.ctrlKey || event.metaKey) return
    event.preventDefault()
    handlers.onBack()
  }

  window.addEventListener('keydown', handleKeyDown)

  return {
    show(): void {
      setText(eyebrow, t('party.card.eyebrow'))
      setText(title, t('menu.party.title'))

      setText(publicEyebrow, t('party.hub.publicTag'))
      setText(publicTitle, t('party.hub.publicTitle'))
      setText(publicDesc, t('party.hub.publicDesc'))
      setText(publicGo, t('party.card.start'))
      publicCard.setAttribute(
        'aria-label',
        t('party.card.aria', { title: t('party.hub.publicTitle'), desc: t('party.hub.publicDesc') }),
      )

      setText(privateEyebrow, t('party.hub.privateTag'))
      setText(privateTitle, t('party.card.title'))
      setText(privateSeats, t('party.card.chipSeats', { max: options.seatsMax }))
      setText(privateLevel, t(`party.level.${options.neighborLevel}`))
      setText(privateDesc, t('party.card.desc'))
      setText(privateGo, t('party.card.start'))
      privateCard.setAttribute(
        'aria-label',
        t('party.card.aria', { title: t('party.card.title'), desc: t('party.card.desc') }),
      )

      setText(rankedEyebrow, t('party.hub.rankedTag'))
      setText(rankedTitle, t('party.hub.rankedTitle'))
      setText(rankedDesc, t('party.hub.rankedDesc'))
      setText(rankedWhy, t('party.hub.rankedLocked'))

      setText(back, t('menu.back'))
      root.hidden = false
      publicCard.focus()
    },

    hide(): void {
      if (root.contains(document.activeElement)) {
        const focused = document.activeElement
        if (focused instanceof HTMLElement) focused.blur()
      }
      root.hidden = true
    },

    dispose(): void {
      window.removeEventListener('keydown', handleKeyDown)
      root.remove()
    },
  }
}
